/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import { Logger } from '@saviynt/common';
import { Box, Button, Card, ModalIsland } from '@saviynt/design-system';
import cx from 'classnames';

import type { RuleRecommendation } from '../../../services';

import { RulesService } from '../../../services/RulesService';
import { normalizeImpactLevel } from '../../../shared/utils';
import { getUserKey } from '../../../utils/cookies';

import RuleCardCondition from './RuleCardCondition/RuleCardCondition';
import RuleCardFooter from './RuleCardFooter/RuleCardFooter';
import RuleCardHeader from './RuleCardHeader/RuleCardHeader';

import './RuleCard.css';

interface RuleCardProps {
  /** Rule data from API */
  rule: RuleRecommendation;
  /** Callback when View Details button is clicked */
  onViewDetails: (ruleId: number) => void;
  /** Additional CSS classes */
  className?: string;
  userAccess?: 'edit' | 'view' | 'none';
}

/**
 * RuleCard component displaying a single rule recommendation
 * using Card structure with header, condition, and footer sections
 *
 * @param props - The component props
 * @returns JSX element containing the rule card
 */
function RuleCard({
  rule,
  onViewDetails,
  className = '',
  userAccess,
}: RuleCardProps) {
  const [isIgnoreModalOpen, setIsIgnoreModalOpen] = useState(false);

  // Calculate if ignore button should be disabled based on user access
  const isIgnoreButtonDisabled = userAccess !== 'edit';
  const [isRejecting, setIsRejecting] = useState(false);

  const handleViewDetails = () => {
    onViewDetails(rule.recommendedObjectKey);
  };

  const handleIgnoreClick = () => {
    setIsIgnoreModalOpen(true);
  };

  const handleIgnoreCancel = () => {
    setIsIgnoreModalOpen(false);
  };

  const handleIgnoreConfirm = async () => {
    const userKey = getUserKey();

    if (!userKey) {
      Logger.error('User key not found, cannot reject rule');

      return;
    }

    setIsRejecting(true);

    try {
      await RulesService.rejectRule(rule.recommendedObjectKey, {
        rejectedBy: parseInt(userKey, 10),
        reason: 'rejected by user',
      });

      // Store success data for banner display on dashboard
      try {
        sessionStorage.setItem(
          'ruleIgnoreSuccess',
          JSON.stringify({
            timestamp: Date.now(),
          })
        );
      } catch (storageError) {
        Logger.warn(
          'SessionStorage failed, reloading without banner',
          storageError
        );
      }

      setIsIgnoreModalOpen(false);
      window.location.reload();
    } catch (error) {
      Logger.error('Failed to reject rule', error);

      // Store error data for banner display on dashboard
      try {
        sessionStorage.setItem(
          'ruleIgnoreError',
          JSON.stringify({
            timestamp: Date.now(),
          })
        );
      } catch (storageError) {
        Logger.warn(
          'SessionStorage failed, reloading without banner',
          storageError
        );
      }

      window.location.reload();
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <>
      <Card className={cx('Rules-RuleCard', className)}>
        {rule.isNew && (
          <Box
            className='Rules-RuleCard--isNew'
            role='status'
            ariaLabel='New rule recommendation'>
            NEW
          </Box>
        )}
        <RuleCardHeader
          userCount={rule.userMatchCount}
          impact={normalizeImpactLevel(rule.impact)}
          onIgnoreButtonClick={handleIgnoreClick}
          isIgnoreButtonDisabled={isIgnoreButtonDisabled}
        />

        <RuleCardCondition
          conditions={rule.conditions}
          actions={rule.actions}
          ruleId={rule.recommendedObjectKey}
        />

        {/* TODO: REVERT - Remove fallback when backend uses yearlyRequestVolume */}
        <RuleCardFooter
          entitlementsCount={rule.entitlements}
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          yearlyRequests={rule.yearlyRequestVolume ?? rule.monthlyRequestVolume}
          actions={rule.actions}
          onViewDetails={handleViewDetails}
        />
      </Card>

      <ModalIsland
        kind='warning'
        alertTitle='Ignore Rule?'
        alertSubtitle="Are you sure you want to ignore this rule recommendation? You can't get it back once it's gone."
        onClose={handleIgnoreCancel}
        isOpen={isIgnoreModalOpen}
        className='Rules-RuleCard-ignoreModal'
        primaryButton={
          <Button
            type='button'
            kind='filled'
            size='medium'
            onClick={handleIgnoreConfirm}>
            {isRejecting ? 'Ignoring...' : 'Ignore Rule'}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            size='medium'
            onClick={handleIgnoreCancel}>
            Cancel
          </Button>
        }
      />
    </>
  );
}

export default RuleCard;
